import { useState } from 'react';
import { toast } from 'sonner';
import { trainerContentApi, type CurriculumTopic } from '@/lib/api/trainer';
import { RichTextEditor } from '@/components/RichTextEditor';
import { isRichTextEmpty } from '@/components/RichText';

/** Always-editable "About this topic" panel — the rich description students
 *  see on the lesson Overview. Renders the current content straight into the
 *  editor; Save persists it. */
export function TopicAboutEditor({ batchId, topic, onSaved }: {
  batchId: string;
  topic: CurriculumTopic;
  onSaved: (updated: CurriculumTopic) => void;
}) {
  const [html, setHtml] = useState(topic.description ?? '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const res = await trainerContentApi.updateTopic(batchId, topic.id, {
        title: topic.title,
        topic_type: topic.topic_type,
        sort_order: topic.sort_order,
        estimated_hours: topic.estimated_hours,
        description: isRichTextEmpty(html) ? '' : html,
      });
      onSaved(res.data);
      toast.success('Description saved');
    } catch {
      toast.error('Failed to save description');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <RichTextEditor value={html} onChange={setHtml} placeholder="What this topic covers, why it matters, what students will learn…" />

      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="rounded-md bg-[#ff5a1f] px-4 py-1.5 text-xs font-medium text-white disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
